import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";

import { useQuery, useMutation } from "@apollo/client";
import client from "client";

import { Formik, Form } from "formik";
import Uri from "jsuri";
import history from "./../../history";

import {
  QUERY_ACCOUNTS,
  QUERY_fbAuthURI,
  DELETE_ACCOUNT,
} from "pages/SourcePage/SourcesQueries";

// import { DELETE_ACCOUNT, QUERY_ACCOUNTS } from "SourcesQueries";

import { BASEURL, HEADERS } from "components/Utils/Constants";

import { selectIcon } from "components/Utils/Utils.js";
import { SourceContext } from "pages/SourcePage/SourcesMasterPage";

import Heading from "components/Elements/Heading";
import Loader from "components/Loader/Loader";
import Accordion from "components/Elements/Accordion";
import { setCookie } from "helpers/Cookie";

const absolute = (uri) => {
  return `${BASEURL}${uri}`;
};

const SourceSelected = (props) => {
  const [sidebarClass, setSidebarClass] = useState(props.sidebar);

  const [connectError, setConnectError] = useState(false);

  const { data, loading, refetch } = useQuery(QUERY_ACCOUNTS, {
    client,
    variables: {
      connector_type: props.sourceData.type,
      tenantID: parseInt(localStorage.defaultTenantID),
    },
  });

  const { data: dataFbAuthURI } = useQuery(QUERY_fbAuthURI, {
    client,
    variables: {
      env: "PROD",
    },
  });

  const sourceRefetch = useContext(SourceContext);

  const [deleteAccount] = useMutation(DELETE_ACCOUNT, {
    client,
    variables: {
      tenantID: parseInt(localStorage.defaultTenantID),
      connector_type: props.sourceData.type,
    },
    onCompleted: () => {
      refetch();
      sourceRefetch.refetchSources();
    },
  });

  const sourcesData = useContext(SourceContext);

  const node = useRef();
  const uri = new Uri(window.location.href);
  const accountName = uri.getQueryParamValue("name");

  const handleEscKey = useCallback(
    (e) => {
      if (e.keyCode === 27) {
        setSidebarClass("sidebar close-selected");
        setTimeout(() => {
          props.closeSidebar();
        }, 600);
        setTimeout(() => {
          sourcesData.enableSourceButton();
        }, 100);
      }
    },
    [props, sourcesData]
  );

  const handleClickOutside = useCallback(
    (e) => {
      if (
        (node.current && node.current.contains(e.target)) ||
        (sourcesData.node.current &&
          sourcesData.node.current.contains(e.target))
      )
        return;
      else {
        setSidebarClass("sidebar close-selected");
        setTimeout(() => {
          props.closeSidebar();
        }, 600);
        setTimeout(() => {
          sourcesData.enableSourceButton();
        }, 100);
      }
    },
    [props, sourcesData]
  );

  useEffect(() => {
    refetch();
    const attachListeners = () => {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
    };
    const timeoutId = setTimeout(attachListeners, 100); // Delay to ensure component is fully mounted
    return () => {
      clearTimeout(timeoutId); // Clean up timeout if component unmounts before the delay
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeHandler = (e) => {
    e.preventDefault();
    setSidebarClass("sidebar close-selected");
    setTimeout(() => {
      props.closeSidebar();
    }, 600);
    setTimeout(() => {
      sourcesData.enableSourceButton();
    }, 100);
  };

  // Function to fetch authentication data based on the source type
  const fetchData = async () => {
    const {
      sourceData: { type },
    } = props;
    const token = window.localStorage.token;
    setCookie("token", token, 60);

    if (["twitter", "tiktok"].includes(type)) {
      const callbackUrl = window.location.href;
      const url = `/auth/${type}?callback=${callbackUrl}`;

      try {
        const response = await fetch(absolute(url), {
          method: "GET",
          headers: HEADERS(token),
        });

        if (response.ok) {
          const data = await response.json();
          window.location.replace(data.url);
        } else {
          setConnectError(
            "Our Authorization App is not configured for your account. Please contact us."
          );
        }
      } catch (error) {
        setConnectError("Error fetching the data.");
      }
    } else if (["facebook", "instagram"].includes(type)) {
      const url = dataFbAuthURI.fbAuthURI.replace(
        /state%3Dgogo/g,
        `state%3D/settings/sources/${type}`
      );
      window.location.replace(url);
    }
  };

  const getImage = (k) => {
    const initials = k.friendly_name
      ? k.friendly_name
          .split(" ")
          .map((n) => n[0])
          .join("")
      : k.name
          .split(" ")
          .map((n) => n[0])
          .join("");
    if (k.url) {
      return (
        <img
          src={k.url}
          alt={k.name}
          onError={(e) => e.target.replaceWith(initials)}
          style={{ width: "100%" }}
        />
      );
    } else {
      return initials;
    }
  };
  return (
    <div ref={node}>
      <div className={sidebarClass}>
        <header className="source-header p-3">
          <div className="row">
            <div className="col-6">
              <div className="row">
                <div className="mr-3">
                  <img
                    src={selectIcon(props.sourceData.type)}
                    alt={props.sourceData.type}
                    width="40"
                    height="40"
                  />
                </div>
                <div>
                  <Heading size={4} className="m-0">
                    {props.sourceData.name}
                  </Heading>
                  <small>
                    {data && data.accounts
                      ? `${data.accounts.length} Accounts Connected`
                      : "No Accounts Connected"}
                  </small>

                  {connectError && (
                    <div className="add-source-error">{connectError}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-6 py-2 d-flex justify-content-end">
              {localStorage.roles &&
                localStorage.roles.includes("admin") &&
                props.sourceData.type !== "youtube" && (
                  <button
                    type="button"
                    className="add-source-button mr-4"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <i className="fas fa-plus plus-sign mr-2" />
                    Connect
                  </button>
                )}
              <button
                type="button"
                className="close-selected"
                onClick={closeHandler}
              >
                &times;
              </button>
            </div>
          </div>
        </header>

        <div className="row d-flex pl-4 source-header__account">
          <div className="source-items">Accounts</div>

          {/* <div className="source-items">Status</div>
          <div className="source-items">Data Mapping</div>
          <div className="source-items">Settings</div>
          <div className="source-items">Help</div> */}
        </div>

        <div className="sources-border-between"></div>

        {accountName && (
          <div className="source-list px-4 pb-5">
            <div className="alert alert-success mr-2 ml-2" role="alert">
              Account {accountName} has been deleted
            </div>
          </div>
        )}

        {loading ? (
          <Loader minHeight={450} col={"col-1"} size="circle-scale--sm" />
        ) : (
          <>
            {[...data.accounts]
              .sort((a, b) =>
                (a.friendly_name || a.name).localeCompare(
                  b.friendly_name || b.name
                )
              )
              .map((k, index) => (
                <Accordion
                  key={k.id}
                  title="What is your return policy?"
                  titleContent={
                    <div className="px-4">
                      <div className="row">
                        <div className="initials text-uppercase text-center mr-2 mt-4">
                          {getImage(k)}
                        </div>
                        <div className="col-4">
                          <Heading size={5} className="pt-4">
                            {k.name}
                          </Heading>
                          <small className="selected-name">
                            {k.friendly_name}
                          </small>
                        </div>

                        <div className="ml-auto pr-3 d-none d-flex align-items-center">
                          <div className="row pt-4 mr-5 text-center">
                            <div
                              className={
                                k.status
                                  ? "status active-indicator"
                                  : "status inactive-indicator"
                              }
                            ></div>
                            {/* TODO: Disabled temporarily the amount of handles related to the account. P2-2121  */}
                            <div>
                              {k.status ? "Active" : "Inactive"}
                              {/*   / {k.handles.length} Handles*/}
                            </div>
                          </div>

                          {/* TODO: Trash button removed temporarily.  P2-2121*/}
                          {props.sourceData.type === "youtube" && (
                            <Formik
                              initialValues={{ id: k.id }}
                              validateOnChange={true}
                              validateOnBlur={true}
                              onSubmit={async ({ setSubmitting }) => {
                                let result = window.confirm(
                                  "Are you sure you want to delete this account?"
                                );
                                if (result) {
                                  const confirmation = await deleteAccount({
                                    variables: {
                                      id: k.id,
                                      connector_type: "youtube",
                                      tenantID: parseInt(
                                        localStorage.defaultTenantID
                                      ),
                                    },
                                  });
                                  if (confirmation.data.deleteAccount) {
                                    history.push(
                                      `/settings/sources?name=${k.name}`
                                    );
                                  }
                                }
                              }}
                            >
                              <Form>
                                <button
                                  className="btn btm-sm mt-4"
                                  type="submit"
                                >
                                  <i className="icon__delete fas fa-trash-alt"></i>
                                </button>
                              </Form>
                            </Formik>
                          )}
                        </div>
                      </div>
                      <div className="border-between"></div>
                    </div>
                  }
                  content={
                    k.handles === null || k.handles.length === 0 ? null : (
                      <div className="card handles">
                        <div className="card-header">
                          <div className="container">
                            <div className="row">
                              <div className="col-sm">Handles</div>
                              <div className="col-sm">Access</div>
                              <div className="col-sm">Downloading Data</div>
                            </div>
                          </div>
                        </div>
                        <ul className="list-group list-group-flush">
                          <li className="list-group-item">
                            <div className="container">
                              {k.handles.length === 0 ? (
                                <div className="row">
                                  <div className="col-sm text-center">
                                    <div>No Handles</div>
                                  </div>
                                </div>
                              ) : (
                                k.handles.map((handle, hkey) => (
                                  <div className="row handles-how" key={hkey}>
                                    <div className="col-sm">
                                      {handle.profile_image_url ? (
                                        <img
                                          className="handles-thumbnail"
                                          src={handle.profile_image_url}
                                          alt={handle.friendly_name}
                                        />
                                      ) : (
                                        <div className="handles-thumbnail-na">
                                          N/A
                                        </div>
                                      )}

                                      <div>{handle.friendly_name}</div>
                                    </div>
                                    <div className="col-sm">
                                      <div className="text-capitalize">
                                        {handle.access}
                                      </div>
                                    </div>
                                    <div className="col-sm">
                                      <div>
                                        {handle.downloading_data ? "Yes" : "No"}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </li>
                        </ul>
                      </div>
                    )
                  }
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
};
export default SourceSelected;
